import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useHistory } from "react-router-dom";
import "./styles.css";
import useSettings from "../../hooks/useSettings";
import ThemeToggle from "./ThemeToggle";
import api, { openApi } from "../../services/api";
import { i18n } from "../../translate/i18n";
import { setSignupIntent } from "../../utils/signupIntent";

const LogoSVG = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="logoGradient" x1="0" y1="0" x2="40" y2="40">
        <stop offset="0%" stopColor="#4F46E5" />
        <stop offset="100%" stopColor="#06B6D4" />
      </linearGradient>
    </defs>
    <rect width="40" height="40" rx="10" fill="url(#logoGradient)" />
    <path d="M20 9L29 14.5V25.5L20 31L11 25.5V14.5L20 9Z" fill="white" opacity="0.95" />
    <circle cx="20" cy="20" r="3" fill="#4F46E5" />
  </svg>
);

const MenuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const fallbackFeatures = [
  {
    title: "Atendimento Multiusuário",
    description: "Distribua tickets em tempo real, organize equipes por fila e acelere a resolução.",
    icon: "👥"
  },
  {
    title: "Campanhas Inteligentes",
    description: "Dispare campanhas com agendamento, recorrência, métricas e controle de status por destinatário.",
    icon: "📣"
  },
  {
    title: "Automação com IA",
    description: "Use IA para respostas automáticas, triagem, roteamento e aumento de conversão.",
    icon: "🤖"
  },
  {
    title: "API e Webhooks",
    description: "Conecte CRM, ERP e fluxos no-code com API REST, webhooks e integrações robustas.",
    icon: "⚡"
  },
  {
    title: "Integrações n8n",
    description: "Construa automações avançadas com gatilhos e ações personalizadas.",
    icon: "🔌"
  },
  {
    title: "Relatórios Avançados",
    description: "Acompanhe desempenho de atendimento, tempos de resposta e produtividade com clareza.",
    icon: "📊"
  },
  {
    title: "Omnichannel",
    description: "Centralize WhatsApp, Instagram e Facebook em uma única operação de atendimento.",
    icon: "💬"
  },
  {
    title: "Multiempresa",
    description: "Gerencie múltiplas operações em ambiente isolado com governança e segurança.",
    icon: "🏢"
  }
];

const fallbackIntegrations = [
  "WhatsApp",
  "Instagram",
  "Facebook",
  "Telegram",
  "OpenAI",
  "n8n",
  "Webhooks",
  "API REST"
];

const Home = () => {
  const history = useHistory();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [customSettings, setCustomSettings] = useState({});
  const [theme, setTheme] = useState("dark");
  const [publicPlans, setPublicPlans] = useState([]);
  const [publicFeatures, setPublicFeatures] = useState(fallbackFeatures);
  const [publicIntegrations, setPublicIntegrations] = useState(fallbackIntegrations);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState("");
  const { getPublicSetting } = useSettings();

  const getCompanyIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const companyId = urlParams.get("companyId");
    return companyId ? parseInt(companyId, 10) : 1;
  };

  useEffect(() => {
    const storedTheme = localStorage.getItem("ticketwhats-theme");
    const systemPrefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    const initialTheme = storedTheme === "light" || storedTheme === "dark"
      ? storedTheme
      : (systemPrefersDark ? "dark" : "light");
    setTheme(initialTheme);
  }, []);

  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyOverflowY = document.body.style.overflowY;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevHtmlOverflowY = document.documentElement.style.overflowY;

    document.body.style.overflow = "auto";
    document.body.style.overflowY = "auto";
    document.documentElement.style.overflow = "auto";
    document.documentElement.style.overflowY = "auto";

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.overflowY = prevBodyOverflowY;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.documentElement.style.overflowY = prevHtmlOverflowY;
    };
  }, []);

  useEffect(() => {
    document.body.setAttribute("data-theme", theme);
    localStorage.setItem("ticketwhats-theme", theme);
  }, [theme]);

  useEffect(() => {
    const loadCustomSettings = async () => {
      try {
        const primaryColor = await getPublicSetting("homePagePrimaryColor");
        const backgroundColor = await getPublicSetting("homePageBackgroundColor");
        const textColor = await getPublicSetting("homePageTextColor");
        const accentColor = await getPublicSetting("homePageAccentColor");

        const fontFamily = await getPublicSetting("homePageFontFamily");
        const fontSize = await getPublicSetting("homePageFontSize");

        const heroTitle = await getPublicSetting("homePageHeroTitle");
        const heroDescription = await getPublicSetting("homePageHeroDescription");
        const heroBadge = await getPublicSetting("homePageHeroBadge");
        const sectionFeaturesTitle = await getPublicSetting("homePageFeaturesTitle");
        const sectionFeaturesDescription = await getPublicSetting("homePageFeaturesDescription");
        const sectionPricingTitle = await getPublicSetting("homePagePricingTitle");
        const sectionPricingDescription = await getPublicSetting("homePagePricingDescription");
        const ctaTitle = await getPublicSetting("homePageCtaTitle");
        const ctaDescription = await getPublicSetting("homePageCtaDescription");

        setCustomSettings({
          primaryColor,
          backgroundColor,
          textColor,
          accentColor,
          fontFamily,
          fontSize,
          heroTitle,
          heroDescription,
          heroBadge,
          sectionFeaturesTitle,
          sectionFeaturesDescription,
          sectionPricingTitle,
          sectionPricingDescription,
          ctaTitle,
          ctaDescription
        });
      } catch (error) {
        console.error("Error loading homepage customization settings:", error);
      }
    };

    loadCustomSettings();
  }, []);

  useEffect(() => {
    const formatCurrency = (amountCents, currency) => {
      const amount = (amountCents || 0) / 100;
      try {
        return new Intl.NumberFormat("pt-BR", { style: "currency", currency: currency || "BRL" }).format(amount);
      } catch {
        return `R$ ${amount.toFixed(2)}`;
      }
    };

    const cycleLabel = (cycle) => {
      const labels = { monthly: "mês", quarterly: "trimestre", semiannual: "semestre", annual: "ano", custom: "período" };
      return labels[cycle] || cycle;
    };

    const normalizePlansPayload = (data) => {
      if (!Array.isArray(data)) return [];
      return data.map((plan, index) => {
        const planFeatures = [];
        if (plan.users) planFeatures.push(`${plan.users} ${plan.users === 1 ? "usuário" : "usuários"}`);
        if (plan.connections) planFeatures.push(`${plan.connections} ${plan.connections === 1 ? "conexão" : "conexões"} WhatsApp`);
        if (plan.queues) planFeatures.push(`${plan.queues} ${plan.queues === 1 ? "fila" : "filas"}`);
        if (plan.useWhatsapp !== false) planFeatures.push("Atendimento via WhatsApp");
        if (plan.useCampaigns) planFeatures.push("Campanhas");
        if (plan.useExternalApi) planFeatures.push("API externa");

        const features = Array.isArray(plan.features) && plan.features.length > 0
          ? plan.features
          : planFeatures;

        const planPublicId = plan.id || plan.publicId;

        return {
          publicId: planPublicId,
          name: plan.name,
          subtitle: plan.description || `Até ${plan.users || 0} usuários`,
          price: formatCurrency(plan.amountCents, plan.currency),
          period: `/${cycleLabel(plan.billingCycle)}`,
          featured: index === 1,
          cta: "Começar Agora",
          features
        };
      });
    };

    const loadPublicPlans = async () => {
      setPlansLoading(true);
      setPlansError("");
      try {
        const response = await openApi.get("/public/billing/plans");
        const rawPlans = Array.isArray(response.data?.data) ? response.data.data : [];
        console.log("[HOME_PUBLIC_BILLING_PLANS_RESPONSE]", response.data);

        const mappedPlans = normalizePlansPayload(rawPlans);
        if (mappedPlans.length > 0) {
          setPublicPlans(mappedPlans);

          const featureSet = new Set(fallbackFeatures.map(f => f.title));
          const mergedFeatures = [...fallbackFeatures];
          mappedPlans.forEach(plan => {
            (plan.features || []).forEach(featureName => {
              if (!featureSet.has(featureName)) {
                featureSet.add(featureName);
                mergedFeatures.push({
                  title: featureName,
                  description: "Disponível conforme o plano contratado.",
                  icon: "✨"
                });
              }
            });
          });
          setPublicFeatures(mergedFeatures);
        } else {
          setPublicPlans([]);
          setPlansError("");
        }
      } catch (error) {
        setPublicPlans([]);
        setPlansError("Não foi possível carregar as ofertas comerciais no momento. Entre em contato com a equipe de atendimento.");
      } finally {
        setPlansLoading(false);
      }
    };

    loadPublicPlans();
  }, []);

  const findScrollableParent = (node) => {
    let parent = node?.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      const isScrollable = (overflowY === "auto" || overflowY === "scroll") && parent.scrollHeight > parent.clientHeight;
      if (isScrollable) return parent;
      parent = parent.parentElement;
    }
    return null;
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    console.log("[HOME_SCROLL_CLICK]", { id: sectionId, found: Boolean(element) });
    if (!element) {
      setMobileMenuOpen(false);
      return;
    }

    const headerOffset = 96;
    const scrollableParent = findScrollableParent(element);

    if (scrollableParent) {
      const parentRect = scrollableParent.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const top = scrollableParent.scrollTop + (elementRect.top - parentRect.top) - headerOffset;
      scrollableParent.scrollTo({ top, behavior: "smooth" });
    } else {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }

    setMobileMenuOpen(false);
  };

  const toggleTheme = () => {
    setTheme(prev => (prev === "dark" ? "light" : "dark"));
  };

  return (
    <div className="landing-page landing-premium">
      <style>
        {`
          .landing-premium {
            ${customSettings.backgroundColor ? `--bg-custom: ${customSettings.backgroundColor};` : ""}
            ${customSettings.textColor ? `--text-custom: ${customSettings.textColor};` : ""}
            ${customSettings.primaryColor ? `--brand-primary: ${customSettings.primaryColor};` : ""}
            ${customSettings.accentColor ? `--brand-accent: ${customSettings.accentColor};` : ""}
            ${customSettings.fontFamily ? `--font-custom: ${customSettings.fontFamily};` : ""}
            ${customSettings.fontSize ? `--font-size-custom: ${customSettings.fontSize}px;` : ""}
          }
        `}
      </style>

      <nav className="landing-navbar">
        <div className="container">
          <RouterLink to="/home" className="navbar-brand" aria-label="TicketWhats Home">
            <LogoSVG size={40} />
            <span className="logo-text">TicketWhats</span>
          </RouterLink>

          <button className="navbar-toggler" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label={i18n.t("home.aria.openMenu")}>
            <MenuIcon />
          </button>

          <ul className={`navbar-nav ${mobileMenuOpen ? "active" : ""}`}>
            <li><button type="button" className="nav-link home-nav-link" onClick={() => scrollToSection("recursos")}>{i18n.t("home.nav.features")}</button></li>
            <li><button type="button" className="nav-link home-nav-link" onClick={() => scrollToSection("integracoes")}>{i18n.t("home.nav.integrations")}</button></li>
            <li><button type="button" className="nav-link home-nav-link" onClick={() => scrollToSection("planos")}>{i18n.t("home.nav.plans")}</button></li>
            <li><button type="button" className="nav-link home-nav-link" onClick={() => scrollToSection("contact")}>{i18n.t("home.nav.contact")}</button></li>
            <li><RouterLink to="/login" className="nav-link">{i18n.t("home.nav.login")}</RouterLink></li>
            <li><RouterLink to="/signup" className="btn-nav-primary">{i18n.t("home.nav.startNow")}</RouterLink></li>
          </ul>
        </div>
      </nav>

      <section className="hero-section" id="hero">
        <div className="hero-background" aria-hidden="true">
          <div className="grid-overlay" />
          <div className="gradient-orb orb-1" />
          <div className="gradient-orb orb-2" />
          <div className="gradient-orb orb-3" />
        </div>

        <div className="container hero-layout">
          <div className="hero-copy">
            <div className="hero-badge">
              <span>⚡</span>
              <span>{customSettings.heroBadge || i18n.t("home.hero.badge")}</span>
            </div>

            <h1 className="hero-title">
              {customSettings.heroTitle || i18n.t("home.hero.title")}
            </h1>

            <p className="hero-description">
              {customSettings.heroDescription || i18n.t("home.hero.description")}
            </p>

            <div className="hero-cta">
              <RouterLink to="/signup" className="btn-hero">
                {i18n.t("home.hero.startFree")}
                <ArrowRightIcon />
              </RouterLink>
              <RouterLink to="/login" className="btn-secondary">{i18n.t("home.hero.viewDemo")}</RouterLink>
            </div>

            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">99.9%</div>
                <div className="stat-label">{i18n.t("home.stats.uptime")}</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">+1M</div>
                <div className="stat-label">{i18n.t("home.stats.messages")}</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">24/7</div>
                <div className="stat-label">{i18n.t("home.stats.support")}</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">+10</div>
                <div className="stat-label">{i18n.t("home.stats.integrations")}</div>
              </div>
            </div>
          </div>

          <div className="hero-visual" aria-label={i18n.t("home.aria.dashboardMockup")}>
            <div className="dashboard-shell glass-card">
              <div className="dashboard-header">
                <span className="dot red" />
                <span className="dot yellow" />
                <span className="dot green" />
                <p>{i18n.t("home.dashboard.title")}</p>
              </div>
              <div className="dashboard-grid">
                <div className="kpi-card">
                  <small>{i18n.t("home.dashboard.activeTickets")}</small>
                  <strong>128</strong>
                </div>
                <div className="kpi-card">
                  <small>{i18n.t("home.dashboard.campaignsToday")}</small>
                  <strong>32</strong>
                </div>
                <div className="chat-stream">
                  <p>{i18n.t("home.dashboard.customerMessage")}</p>
                  <p>{i18n.t("home.dashboard.botMessage")}</p>
                  <p>{i18n.t("home.dashboard.agentMessage")}</p>
                </div>
                <div className="chart-card">
                  <div className="chart-bar b1" />
                  <div className="chart-bar b2" />
                  <div className="chart-bar b3" />
                  <div className="chart-bar b4" />
                </div>
              </div>
            </div>

            <div className="floating-pill pill-a">{i18n.t("home.floating.conversion")}</div>
            <div className="floating-pill pill-b">{i18n.t("home.floating.aiActive")}</div>
            <div className="floating-pill pill-c">{i18n.t("home.floating.apiOnline")}</div>
          </div>
        </div>
      </section>

      <section id="recursos" className="features-section section-anchor">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{customSettings.sectionFeaturesTitle || i18n.t("home.features.title")}</h2>
            <p className="section-description">{customSettings.sectionFeaturesDescription || i18n.t("home.features.description")}</p>
          </div>
          <div className="features-grid">
            {publicFeatures.length > 0 ? publicFeatures.map((feature) => (
              <article key={feature.title} className="feature-card glass-card">
                <div className="feature-icon" aria-hidden="true">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            )) : (
              <div className="home-empty-state">
                {i18n.t("home.features.empty")}
              </div>
            )}
          </div>
        </div>
      </section>

      <section id="integracoes" className="integrations-section section-anchor">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{i18n.t("home.integrations.title")}</h2>
            <p className="section-description">{i18n.t("home.integrations.description")}</p>
          </div>
          <div className="integrations-grid">
            {publicIntegrations.length > 0 ? publicIntegrations.map((integration) => (
              <div key={integration} className="integration-chip glass-card">{integration}</div>
            )) : (
              <div className="home-empty-state">
                {i18n.t("home.integrations.empty")}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="ai-section">
        <div className="container ai-layout">
          <div>
            <h2 className="section-title">{i18n.t("home.ai.title")}</h2>
            <p className="section-description align-left">
              {i18n.t("home.ai.description")}
            </p>
            <ul className="ai-list">
              <li>{i18n.t("home.ai.items.contextReplies")}</li>
              <li>{i18n.t("home.ai.items.priorityRouting")}</li>
              <li>{i18n.t("home.ai.items.smartWorkflows")}</li>
              <li>{i18n.t("home.ai.items.realtimeInsights")}</li>
            </ul>
          </div>
          <div className="ai-panel glass-card">
            <div className="ai-head">{i18n.t("home.ai.panelTitle")}</div>
            <div className="ai-message user">{i18n.t("home.ai.sampleUser")}</div>
            <div className="ai-message bot">{i18n.t("home.ai.sampleBotOne")}</div>
            <div className="ai-message bot">{i18n.t("home.ai.sampleBotTwo")}</div>
          </div>
        </div>
      </section>

      <section id="planos" className="pricing-section section-anchor">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{customSettings.sectionPricingTitle || i18n.t("home.pricing.title")}</h2>
            <p className="section-description">{customSettings.sectionPricingDescription || i18n.t("home.pricing.description")}</p>
          </div>
          <div className="pricing-grid">
            {plansLoading && <p className="section-description">{i18n.t("home.pricing.loading")}</p>}
            {!plansLoading && plansError && <p className="section-description">{plansError}</p>}
            {!plansLoading && publicPlans.map((plan) => (
              <article key={plan.name} className={`pricing-card glass-card ${plan.featured ? "featured" : ""}`}>
                {plan.featured && <span className="popular-badge">{i18n.t("home.pricing.featured")}</span>}
                <h3 className="plan-name">{plan.name}</h3>
                <p className="plan-description">{plan.subtitle}</p>
                <div className="plan-price"><span>{plan.price}</span>{plan.period}</div>
                <ul className="plan-features">
                  {plan.features.map((feature) => <li key={feature}>{feature}</li>)}
                </ul>
                <button
                  type="button"
                  className="btn-plan"
                  onClick={() => {
                    setSignupIntent({
                      planPublicId: plan.publicId,
                      planName: plan.name,
                      amountCents: plan.amountCents,
                      currency: plan.currency,
                      billingCycle: plan.billingCycle,
                      users: plan.users,
                      connections: plan.connections,
                    });
                    history.push("/signup");
                  }}
                >
                  {plan.cta}
                </button>
              </article>
            ))}
            {!plansLoading && !plansError && publicPlans.length === 0 && (
              <div className="home-empty-state">
                {i18n.t("home.pricing.empty")}
              </div>
            )}
          </div>
        </div>
      </section>

      <section id="contact" className="cta-section section-anchor">
        <div className="container cta-box glass-card">
          <h2 className="cta-title">{customSettings.ctaTitle || i18n.t("home.cta.title")}</h2>
          <p className="cta-description">{customSettings.ctaDescription || i18n.t("home.cta.description")}</p>
          <div className="cta-buttons">
            <RouterLink to="/signup" className="btn-hero">{i18n.t("home.nav.startNow")}</RouterLink>
            <RouterLink to="/login" className="btn-secondary">{i18n.t("home.cta.viewPlatform")}</RouterLink>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="container">
          <div className="footer-grid">
            <div>
              <div className="footer-logo">
                <LogoSVG size={30} />
                <span>TicketWhats</span>
              </div>
              <p className="footer-description">{i18n.t("home.footer.description")}</p>
            </div>
            <div>
              <h4>{i18n.t("home.footer.product")}</h4>
              <ul>
                <li><button onClick={() => scrollToSection("recursos")}>{i18n.t("home.nav.features")}</button></li>
                <li><button onClick={() => scrollToSection("integracoes")}>{i18n.t("home.nav.integrations")}</button></li>
                <li><button onClick={() => scrollToSection("planos")}>{i18n.t("home.nav.plans")}</button></li>
              </ul>
            </div>
            <div>
              <h4>{i18n.t("home.footer.company")}</h4>
              <ul>
                <li><a href="/login">{i18n.t("home.nav.login")}</a></li>
                <li><a href="/signup">{i18n.t("home.footer.createAccount")}</a></li>
                <li><a href="#status">{i18n.t("common.statusLabel")}</a></li>
              </ul>
            </div>
            <div>
              <h4>{i18n.t("home.footer.support")}</h4>
              <ul>
                <li><a href="#docs">{i18n.t("home.footer.docs")}</a></li>
                <li><a href="#api">{i18n.t("home.footer.api")}</a></li>
                <li><a href="#terms">{i18n.t("home.footer.terms")}</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>{i18n.t("home.footer.copyright", { year: new Date().getFullYear() })}</p>
          </div>
        </div>
      </footer>

      <ThemeToggle theme={theme} onToggle={toggleTheme} />
    </div>
  );
};

export default Home;
